import React, { useEffect, useState } from "react";
import { EntertainmentType } from "@/interfaces/IUniqueShip";
import { toKebabCase, toTitleCaseV2 } from "@/utils/helpers";
import Grid from "@mui/material/Grid2";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItemButton,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { SwiperComponent } from "@/components/common/SwiperComponent";
import { Contents } from "./CruiseFacilities";

import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowNextIcon from "@mui/icons-material/ArrowForwardRounded";

const CruiseOtherFacilities: React.FC<{
  data?: EntertainmentType[];
  name?: string;
  selectedTab?: string;
}> = ({ data, name, selectedTab }) => {
  const [isReadMore, setIsReadMore] = useState(false);

  const [facilityTypeTabValue, setFacilityTypeTabValue] = useState<string>(
    `${toKebabCase(data![0]?.name ?? "")}_1`,
  );

  useEffect(() => {
    setFacilityTypeTabValue(`${toKebabCase(data![0]?.name ?? "")}_1`);
  }, [name]);

  const [sectionID, setSectionID] = useState("");

  const isMobile = useMediaQuery("(max-width: 900px)");

  return (
    <>
      {isMobile ? (
        <>
          <Box
            sx={{
              px: "0rem",
              borderRadius: "16px",
              position: "relative",
              display: { xs: "block", md: "none" },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 9 }}>
              {!!data &&
                data?.length > 0 &&
                data?.map((facility, facilityIndex) => (
                  <CruiseAccordionMobile key={facilityIndex} data={facility} />
                ))}
            </Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                top: "-1px",
                left: "-1px",
                zIndex: 10,
                clipPath:
                  'path("M20.4883 0.326172C9.44275 0.326172 0.488545 9.27972 0.488281 20.3252V0.326172H20.4883Z")',
              }}
            ></Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                top: "-1px",
                right: "0px",
                zIndex: 10,
                clipPath:
                  'path("M20.7422 20.3262C20.7422 9.28064 11.7877 0.326435 0.742188 0.326171L20.7422 0.326172L20.7422 20.3262Z")',
              }}
            ></Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                bottom: "0px",
                left: "-1px",
                zIndex: 10,
                clipPath:
                  'path("M0.48828 0.65332C0.488281 11.6989 9.44281 20.6531 20.4883 20.6533L0.488281 20.6533L0.48828 0.65332Z")',
              }}
            ></Box>

            <Box
              sx={{
                bgcolor: "#fff",
                width: "20px",
                height: "20px",
                position: "absolute",
                bottom: "0px",
                right: "0px",
                zIndex: 10,
                clipPath:
                  'path("M0.742188 20.6523C11.7877 20.6523 20.7419 11.6988 20.7422 0.65332L20.7422 20.6523L0.742188 20.6523Z")',
              }}
            ></Box>
          </Box>
        </>
      ) : (
        <Grid
          container
          size={{ xs: 12 }}
          sx={{
            mt: "2.5rem",
            display: { xs: "none", md: "flex" },
          }}
          spacing={2}
        >
          {!!data && data?.length > 0 && (
            <Grid size={{ xs: 12, md: 3 }}>
              <Box
                sx={{
                  position: "sticky",
                  top: "185px",
                  height: "calc(100vh - 310px)",
                  overflow: "auto",
                }}
              >
                <List component="div" disablePadding>
                  {data?.map((item, facilityIndex) => (
                    <ListItemButton
                      key={facilityIndex}
                      sx={{
                        mb: "8px",
                        display: "flex",
                        justifyContent: "space-between",
                        borderRadius: "8px",
                        fontFamily: "'blauerNue', 'blauerNue Fallback'",
                        p: "8px 10px 8px 16px",
                        bgcolor: "#fff",
                        color: "primary.main",
                        border: "2px solid #cfcdff",
                        transition: "all 0.3s",
                        "&:hover": {
                          bgcolor: "#f3f3f3",
                          color: "#3000ff",
                          border: "2px solid #3000ff",
                        },
                        ...(facilityTypeTabValue ===
                          `${toKebabCase(item?.name ?? "")}_${facilityIndex + 1}` && {
                          bgcolor: "#E30053",
                          color: "#fff",
                          border: "2px solid #E30053",
                          "&:hover": {
                            bgcolor: "#E30053",
                            color: "#fff",
                            border: "2px solid #E30053",
                          },
                        }),
                      }}
                      onClick={() => {
                        setFacilityTypeTabValue(
                          `${toKebabCase(item?.name ?? "")}_${facilityIndex + 1}`,
                        );
                        setIsReadMore(false);
                      }}
                    >
                      <Box component="span">{toTitleCaseV2(item?.name ?? "") ?? ""}</Box>

                      <Box
                        sx={{
                          display: "grid",
                          placeItems: "center",
                          color: "#fff",
                          borderRadius: "16px",
                          height: "30px",
                          svg: {
                            width: "30px",
                            height: "30px",
                          },
                        }}
                      >
                        {facilityTypeTabValue ===
                        `${toKebabCase(item?.name ?? "")}_${facilityIndex + 1}` ? (
                          <ArrowNextIcon />
                        ) : (
                          <></>
                        )}
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 9 }} spacing={2}>
            {data
              ?.filter(
                (item, index) =>
                  `${toKebabCase(item?.name ?? "")}_${index + 1}` === facilityTypeTabValue,
              )
              ?.map((facility, facilityIndex) => (
                <Box key={facilityIndex} id={`${toKebabCase(facility?.name ?? "")}`}>
                  <Box sx={{ position: "relative" }}>
                    <Typography
                      variant="h3"
                      align="center"
                      gutterBottom
                      sx={{
                        fontSize: { xs: "1.1rem", md: "2rem" },
                        color: "#060273",
                        textAlign: "left",
                        width: "100%",
                        mb: "16px",
                      }}
                    >
                      {toTitleCaseV2(facility?.name ?? "")}
                    </Typography>

                    <Box sx={{ width: "100%", display: "block" }}>
                      {(facility?.images?.filter((x) => x.href != "").length ?? 0) > 0 && (
                        <Box sx={{ width: { xs: "100%", md: "50%" }, float: "left", mr: "1.2rem" }}>
                          <SwiperComponent
                            images={facility?.images
                              ?.filter((x) => x.href != "")
                              ?.map((x) => ({ ...x, type: "image" }))}
                          />
                        </Box>
                      )}

                      <Box sx={{ position: "relative" }}>
                        <Contents
                          data={facility?.description ?? ""}
                          isReadMore={isReadMore}
                          sectionID={sectionID}
                          onClick={() => {
                            setIsReadMore((prev) => !prev);
                            setSectionID(selectedTab ?? "");
                          }}
                          isTypesOfFacilities
                          setIsReadMore={setIsReadMore}
                          setSectionID={setSectionID}
                          paragraphID={toKebabCase(facility?.name ?? "") ?? ""}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default CruiseOtherFacilities;

const CruiseAccordionMobile: React.FC<{
  data: EntertainmentType;
}> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Accordion
        expanded={expanded}
        disableGutters
        sx={{
          bgcolor: "#060273",
          color: "#fff",
          overflow: "unset",
          ".MuiAccordionSummary-expandIconWrapper": {
            bgcolor: "#fff",
            border: "2px solid #ffffffaa",
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            svg: {
              width: "22px",
              height: "22px",
              color: "primary.main",
            },
          },
          "&.MuiPaper-root.MuiAccordion-root::before": {
            bgcolor: "#fff",
            zIndex: 120,
          },
          "&.MuiPaper-root.MuiAccordion-root.Mui-expanded::before": {
            opacity: 1,
          },
          "&:first-of-type": {
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          },
          "&:last-of-type": {
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          },
          ".MuiAccordionDetails-root": {
            pt: "0px",
            px: "0px",
            pb: "0px",
            borderBottom: "2px solid #ffffffaa",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandIcon />}
          sx={{
            background: expanded
              ? "linear-gradient(90deg,rgba(89, 0, 255, 1) 0%, rgba(46, 0, 130, 1) 100%)"
              : "primary.main",
            position: "sticky",
            top: "145px",
            zIndex: 100,
          }}
          onClick={() => {
            setExpanded((prev) => !prev);
          }}
        >
          <Box
            sx={{
              color: expanded ? "#fff" : "#fff",
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontSize: "1.3rem",
              fontWeight: 600,
              position: "relative",
              zIndex: 10,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "2px 10px",
              width: "100%",
            }}
          >
            <Box>{data?.name}</Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <MobileFacilityCard data={data} />
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export const MobileFacilityCard: React.FC<{
  data: EntertainmentType;
}> = ({ data }) => {
  const [isReadMore, setIsReadMore] = useState(false);

  return (
    <Box
      sx={{
        borderTop: "1px solid #554fff",
        bgcolor: "#030047",
        p: "16px",
        position: "relative",
        zIndex: 10,
      }}
    >
      <Box sx={{ width: "100%", display: "block" }}>
        <Box
          sx={{
            width: "100%",
            mb: "8px",
            position: "relative",
          }}
        >
          <SwiperComponent
            isItineraryMobile
            images={data?.images?.filter((x) => x.href != "").map((x) => ({ ...x, type: "image" }))}
          />
        </Box>

        <Box sx={{ position: "relative" }}>
          <Contents
            data={data?.description ?? ""}
            isReadMore={isReadMore}
            onClick={() => {
              setIsReadMore((prev) => !prev);
            }}
            isTypesOfFacilities
            setIsReadMore={setIsReadMore}
            paragraphID={toKebabCase(data?.name ?? "") ?? ""}
            isItineraryMobile
            drawerTitle={data.name ?? ""}
          />
        </Box>
      </Box>
    </Box>
  );
};
